<script setup lang="ts">
// Libraries import
import { computed, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

// Data and type import
import { type IMissionList, MissionCommand, WorkerMissionStatus } from '@/types/mission';

import type { IWorkerSelection } from "@/modules/missions/types";

import Button from "@/dsComponents/buttons/Button.vue";
import TextareaInput from "@/dsComponents/inputs/TextareaInput.vue";
import Modal from "@/dsComponents/modals/Modal.vue";

import InterimMiniReadModal from "@/components/modal/InterimMiniReadModal.vue";
// Components import
import WorkersSelectionByStatus from "@/modules/missions/components/matching/WorkersSelectionByStatus.vue";

interface MissionWorkersSelectionProps {
    mission: IMissionList;
    workers: IWorkerSelection[];
}

const props = defineProps<MissionWorkersSelectionProps>();
const { t } = useI18n();
const emits = defineEmits<(event: "changeWorkerAction", workerId: string[], action: MissionCommand, reason?: string) => void>();
const interimMiniReadOpen = ref(false);
const interimMiniReadWorkerId = ref("");

const statusOrder = [
    WorkerMissionStatus.CONFIRMED,
    WorkerMissionStatus.ACCEPTED,
    WorkerMissionStatus.REPROPOSED,
    WorkerMissionStatus.REPROPOSED_BY_SMS,
    WorkerMissionStatus.PROPOSED,
    WorkerMissionStatus.ABORTED,
    WorkerMissionStatus.REJECTED_BY_AGENCY,
    WorkerMissionStatus.NOT_CONFIRMED,
    WorkerMissionStatus.DECLINED,
];

const statuses = computed(() => {
    return [...new Set([...props.workers.map((worker) => worker.status), WorkerMissionStatus.CONFIRMED])].sort((a, b) => {
        const indexA = statusOrder.indexOf(a);
        const indexB = statusOrder.indexOf(b);

        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;

        return indexA - indexB;
    });
});

const positionQuantity = computed(() => {
    return props.mission.positions?.length ? props.mission.positions[0].quantity : 0;
});

const workerRejectModalDisplay = ref(false);
const rejectedWorkerId = ref<string[]>([""]);
const rejectReason = ref("");

const confirmedWorkerCount = computed(() => props.workers.filter((e) => e.status === WorkerMissionStatus.CONFIRMED).length);
const statusOpenMap = ref<Partial<Record<WorkerMissionStatus, boolean>>>({});
const initStatusOpen = () => {
    statusOpenMap.value = Object.fromEntries(statusOrder.map((key) => [key, checkIsOpen(key)]));
};
const handleNewWorkerStatusOpen = (excludeStatus: WorkerMissionStatus[]) => {
    statuses.value.forEach((key) => {
        if (!excludeStatus.includes(key)) {
            statusOpenMap.value[key] = checkIsOpen(key);
        }
    });
};

const checkIsOpen = (status: WorkerMissionStatus) => {
    if (status !== WorkerMissionStatus.CONFIRMED && confirmedWorkerCount.value >= positionQuantity.value) return false;
    if ([WorkerMissionStatus.ACCEPTED, WorkerMissionStatus.PROPOSED, WorkerMissionStatus.REPROPOSED, WorkerMissionStatus.CONFIRMED].includes(status))
        return true;
    return false;
};

const handleChangeWorkerAction = (workerId: string[], action: MissionCommand) => {
    if ([MissionCommand.workerAgencyReject].includes(action)) {
        workerRejectModalDisplay.value = true;
        rejectedWorkerId.value = workerId;
        return;
    }

    if (action === MissionCommand.workerConfirm) {
        Object.keys(statusOpenMap).forEach((key) => {
            statusOpenMap.value[key as WorkerMissionStatus] = key === WorkerMissionStatus.CONFIRMED;
        });
    }

    emits("changeWorkerAction", workerId, action);
};

const submitReject = () => {
    emits("changeWorkerAction", rejectedWorkerId.value, MissionCommand.workerAgencyReject, rejectReason.value);
    closeRejecModal();
};

const closeRejecModal = () => {
    workerRejectModalDisplay.value = false;
    rejectReason.value = "";
};

onBeforeMount(() => {
    initStatusOpen();
});

watch(
    () => statuses.value,
    (newVal, oldVal) => {
        const intersection = newVal.filter((value) => oldVal.includes(value));
        handleNewWorkerStatusOpen(intersection);
    }
);

const handleToggleShow = (status: WorkerMissionStatus) => {
    statusOpenMap.value[status] = !statusOpenMap.value[status];
};

const openMiniRead = (workerId: string) => {
    interimMiniReadWorkerId.value = workerId;
    interimMiniReadOpen.value = true;
};

const closeMiniRead = () => {
    interimMiniReadOpen.value = false;
    interimMiniReadWorkerId.value = "";
};
</script>
<template>
    <div>
        <WorkersSelectionByStatus
            v-for="status of statuses"
            :key="status"
            :status="status"
            :workers="props.workers.filter((worker) => worker.status === status)"
            :missionsAvailableCommands="mission.availableCommands"
            :positionQuantity="positionQuantity"
            @onWorkerAction="handleChangeWorkerAction"
            :isOpen="statusOpenMap[status] || false"
            @toggleShow="handleToggleShow"
            @openMiniRead="openMiniRead"
        />
        <Modal :open="workerRejectModalDisplay" :title="t('modals.workerReject.agency.title')" :action="true" width="500px">
            <p>{{ t("modals.workerReject.agency.description") }}</p>
            <TextareaInput :label="t('form.message')" v-model="rejectReason" :rows="2" />
            <template #footer>
                <div class="boutton-container">
                    <Button size="sm" type="neutral" variant="ghost" :label="t('global.actions.cancel')" @click="closeRejecModal" />
                    <Button size="sm" type="primary" variant="filled" :label="t('global.actions.confirm')" @click="submitReject" />
                </div>
            </template>
        </Modal>
        <InterimMiniReadModal
            v-if="interimMiniReadWorkerId"
            @close="closeMiniRead"
            :mission="mission"
            :interimId="interimMiniReadWorkerId"
            :open="interimMiniReadOpen"
        />
    </div>
</template>
<style scoped lang="scss">
.boutton-container {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
}
</style>
